import React from 'react';
import { ExtraIconKind } from '../../utils/extraIcons';
import { ModalityIncludeIcon } from '../ModalitySelectionStep/ModalityIcons';
import { ModalityIncludeKind } from '../../utils/modalityIncludes';

const svgProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true as const,
};

/** Extras that also appear in modality "Incluye" must reuse those exact icons. */
function toModalityIncludeKind(kind: ExtraIconKind): ModalityIncludeKind | null {
    switch (kind) {
        case 'cleaning':
            return 'cleaning_room';
        case 'cleaning_terrace':
            return 'cleaning_terrace';
        case 'merienda':
            return 'merienda';
        case 'decoration':
            return 'decoration';
        case 'monitor':
            return 'monitor';
        default:
            return null;
    }
}

export const ExtraIcon: React.FC<{ kind: ExtraIconKind }> = ({ kind }) => {
    const modalityKind = toModalityIncludeKind(kind);
    if (modalityKind) {
        return <ModalityIncludeIcon kind={modalityKind} />;
    }

    switch (kind) {
        case 'hours':
            // Clock with bold hands
            return (
                <svg {...svgProps}>
                    <circle cx="12" cy="12" r="8.2" />
                    <path d="M12 7.2v5.3l3.6 2" />
                    <circle cx="12" cy="12" r="0.85" fill="currentColor" stroke="none" />
                </svg>
            );
        case 'perrito':
            // Hot dog in a bun
            return (
                <svg {...svgProps}>
                    <path d="M5.2 11.2c1.6-2.6 4.6-4 6.8-4s5.2 1.4 6.8 4c.5.8.1 1.9-.8 2.2H6c-.9-.3-1.3-1.4-.8-2.2Z" />
                    <path d="M6.4 13.2c1.4 1.8 3.6 2.8 5.6 2.8s4.2-1 5.6-2.8" />
                    <path d="M8.2 11.6c.6-1.5 1.7-2.4 3-2.4" />
                    <path d="M15.8 11.6c-.6-1.5-1.7-2.4-3-2.4" />
                    <path d="M9 14.2h6" />
                    <path d="M8.4 12.6h1.2" />
                    <path d="M11.4 12.2h1.2" />
                    <path d="M14.4 12.6h1.2" />
                </svg>
            );
        case 'sandwich':
            // Layered sandwich / snack box
            return (
                <svg {...svgProps}>
                    <path d="M5.5 10.2c1.8-2 4-3 6.5-3s4.7 1 6.5 3v1.2H5.5V10.2Z" />
                    <path d="M5.5 11.4h13v2.2H5.5z" />
                    <path d="M5.5 13.6h13v3.4c0 .7-.5 1.2-1.2 1.2H6.7c-.7 0-1.2-.5-1.2-1.2v-3.4Z" />
                    <path d="M8 15h8" />
                    <path d="M7.5 12.5h9" />
                </svg>
            );
        case 'catering':
            // Covered serving tray
            return (
                <svg {...svgProps}>
                    <path d="M4.5 12.2h15" />
                    <path d="M6 12.2c.5-3.2 2.9-5.4 6-5.4s5.5 2.2 6 5.4" />
                    <path d="M12 4.8v2" />
                    <path d="M5.5 12.2v1.6c0 .8.6 1.4 1.4 1.4h10.2c.8 0 1.4-.6 1.4-1.4v-1.6" />
                    <path d="M9.2 15.2v2.6" />
                    <path d="M14.8 15.2v2.6" />
                    <path d="M8.2 17.8h7.6" />
                </svg>
            );
        case 'mago':
            // Magician hat with stars
            return (
                <svg {...svgProps}>
                    <path d="M5.8 18.8h12.4l-1.6-8.2a1.4 1.4 0 0 0-1.4-1.1H8.8a1.4 1.4 0 0 0-1.4 1.1L5.8 18.8Z" />
                    <path d="M7.2 10.8c1.2-1.1 2.8-1.7 4.8-1.7s3.6.6 4.8 1.7" />
                    <path d="M9.2 9.5 12 4.6l2.8 4.9" />
                    <path d="M12 4.6v1.8" />
                    <path d="m16.4 5.2.5 1.4 1.4.5-1.4.5-.5 1.4-.5-1.4-1.4-.5 1.4-.5Z" fill="currentColor" stroke="none" />
                    <path d="m19 8.4.35 1 .95.35-.95.35-.35 1-.35-1-.95-.35.95-.35Z" fill="currentColor" stroke="none" />
                </svg>
            );
        case 'payaso':
            // Clown face with bow-ish cheeks
            return (
                <svg {...svgProps}>
                    <circle cx="12" cy="11.2" r="6.4" />
                    <path d="M7.6 8.6c1-.9 2.2-1.3 3.4-.9" />
                    <path d="M16.4 8.6c-1-.9-2.2-1.3-3.4-.9" />
                    <circle cx="9.5" cy="11.1" r="0.85" fill="currentColor" stroke="none" />
                    <circle cx="14.5" cy="11.1" r="0.85" fill="currentColor" stroke="none" />
                    <circle cx="12" cy="13" r="0.7" />
                    <path d="M9.4 14.6c.9 1.3 1.9 1.9 2.6 1.9s1.7-.6 2.6-1.9" />
                    <path d="M6.8 16.8c1.4 1.8 3.1 2.7 5.2 2.7s3.8-.9 5.2-2.7" />
                    <path d="M8.2 6.4c.6-.9 1.5-1.4 2.4-1.4" />
                    <path d="M15.8 6.4c-.6-.9-1.5-1.4-2.4-1.4" />
                </svg>
            );
        case 'castillo':
            // Bouncy castle arches
            return (
                <svg {...svgProps}>
                    <path d="M3.8 19.2h16.4" />
                    <path d="M5 19.2V11.4c0-2.2 1.6-4 3.5-4h1.2c1 0 1.9.5 2.3 1.3.4-.8 1.3-1.3 2.3-1.3h1.2c1.9 0 3.5 1.8 3.5 4v7.8" />
                    <path d="M5 14.2h14" />
                    <path d="M9.2 7.4V5.6" />
                    <path d="M14.8 7.4V5.6" />
                    <path d="M8.4 5.6h1.6" />
                    <path d="M14 5.6h1.6" />
                    <path d="M10.6 19.2v-3.2a1.4 1.4 0 0 1 2.8 0v3.2" />
                </svg>
            );
        case 'pizza':
            // Pizza slice
            return (
                <svg {...svgProps}>
                    <path d="M12 3.2 4.4 19.4h15.2L12 3.2Z" />
                    <path d="M7.6 16.6c1.4-1.2 3.2-1.9 5.2-1.9" />
                    <circle cx="10.4" cy="11.8" r="0.85" fill="currentColor" stroke="none" />
                    <circle cx="13.6" cy="14.4" r="0.75" fill="currentColor" stroke="none" />
                    <circle cx="12.2" cy="9.4" r="0.7" fill="currentColor" stroke="none" />
                </svg>
            );
        case 'escape':
            // Door with keyhole
            return (
                <svg {...svgProps}>
                    <rect x="6.5" y="3.8" width="11" height="16.4" rx="1.4" />
                    <circle cx="14.2" cy="12" r="1.15" />
                    <path d="M14.2 13.15v2.1" />
                    <path d="M9.2 12h1.4" />
                </svg>
            );
        case 'cluedo':
            // Magnifying glass + question
            return (
                <svg {...svgProps}>
                    <circle cx="10.2" cy="10.2" r="4.6" />
                    <path d="M13.5 13.5 19 19" />
                    <path d="M8.6 9.2c.3-.9 1-1.5 2-1.5 1.1 0 1.9.7 1.9 1.7 0 1.1-.9 1.5-1.5 1.9" />
                    <circle cx="10.5" cy="13.1" r="0.55" fill="currentColor" stroke="none" />
                </svg>
            );
        case 'carrito':
            // Food cart
            return (
                <svg {...svgProps}>
                    <path d="M4.2 8.2h2.4l1.5 8.6h9.8l1.8-6.2H8.2" />
                    <path d="M9.4 7.2h7.2v3.4H9.4z" />
                    <path d="M11 5.4h4" />
                    <path d="M13 4.2v1.2" />
                    <circle cx="10.2" cy="18.6" r="1.25" />
                    <circle cx="16.4" cy="18.6" r="1.25" />
                </svg>
            );
        case 'nino':
            // Child with plus badge
            return (
                <svg {...svgProps}>
                    <circle cx="10.2" cy="8" r="2.5" />
                    <path d="M5.6 18.6v-.6A4.6 4.6 0 0 1 10.2 13.4a4.6 4.6 0 0 1 4.6 4.6v.6" />
                    <circle cx="17.2" cy="9.4" r="3.1" />
                    <path d="M17.2 7.8v3.2" />
                    <path d="M15.6 9.4h3.2" />
                </svg>
            );
        case 'personaje':
            // Character / costume bust with cape hint
            return (
                <svg {...svgProps}>
                    <circle cx="12" cy="8" r="2.6" />
                    <path d="M6.8 19.2c.4-3.4 2.5-5.4 5.2-5.4s4.8 2 5.2 5.4" />
                    <path d="M7.4 14.2c-1.2.4-2.2 1.4-2.6 2.8" />
                    <path d="M16.6 14.2c1.2.4 2.2 1.4 2.6 2.8" />
                    <path d="M9.4 5.4c.8-.8 1.7-1.2 2.6-1.2s1.8.4 2.6 1.2" />
                </svg>
            );
        case 'camarero':
            // Waiter with tray
            return (
                <svg {...svgProps}>
                    <circle cx="12" cy="6.8" r="2.3" />
                    <path d="M7.8 18.8v-.5A4.2 4.2 0 0 1 12 14.1a4.2 4.2 0 0 1 4.2 4.2v.5" />
                    <path d="M5.4 12.4h13.2" />
                    <path d="M7 12.4c.3-1.4 1.4-2.4 2.8-2.4h4.4c1.4 0 2.5 1 2.8 2.4" />
                    <path d="M12 12.4v1.7" />
                </svg>
            );
        case 'slime':
            // Slime jar / blob
            return (
                <svg {...svgProps}>
                    <path d="M8.2 8.4c0-2.4 1.7-4.2 3.8-4.2s3.8 1.8 3.8 4.2c0 .9-.3 1.7-.7 2.4l1.2 8.2H7.7l1.2-8.2c-.4-.7-.7-1.5-.7-2.4Z" />
                    <path d="M8.4 14.8h7.2" />
                    <path d="M8.1 17.4h7.8" />
                    <circle cx="10.6" cy="11.2" r="0.55" fill="currentColor" stroke="none" />
                    <circle cx="13.4" cy="12.2" r="0.45" fill="currentColor" stroke="none" />
                </svg>
            );
        case 'almacenaje':
            // Storage box
            return (
                <svg {...svgProps}>
                    <path d="M4.2 8.4 12 4.2l7.8 4.2v9.2L12 20.8 4.2 17.6V8.4Z" />
                    <path d="M12 12.4v8.4" />
                    <path d="M4.2 8.4 12 12.4l7.8-4" />
                    <path d="M8.4 10.6h7.2" />
                </svg>
            );
        case 'gift':
            // Gift box (section header)
            return (
                <svg {...svgProps}>
                    <rect x="4.4" y="10.2" width="15.2" height="9.4" rx="1.3" />
                    <path d="M12 10.2v9.4" />
                    <path d="M4.4 14.2h15.2" />
                    <path d="M12 10.2c-1.9-2.6-4.4-3-5.5-1.7S6.2 11.8 8.4 12.2" />
                    <path d="M12 10.2c1.9-2.6 4.4-3 5.5-1.7s-.4 3.2 1.7 3.7" />
                </svg>
            );
        default:
            return (
                <svg {...svgProps}>
                    <circle cx="12" cy="12" r="8" />
                    <path d="M12 8v4.4" />
                    <circle cx="12" cy="15.4" r="0.75" fill="currentColor" stroke="none" />
                </svg>
            );
    }
};

export const GiftSectionIcon: React.FC = () => <ExtraIcon kind="gift" />;

export const CheckIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
            d="m6.5 12.2 3.4 3.4 7.6-7.6"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export const PlusIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
            d="M12 6.5v11M6.5 12h11"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
        />
    </svg>
);
